import React from "react";
import { Grid, Box, Typography } from "@mui/material";
import GroupsIcon from "@mui/icons-material/Groups";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import SavingsIcon from "@mui/icons-material/Savings";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import DashboardKpiCard from "./DashboardKpiCard";
import DashboardChartCard from "./DashboardChartCard";
import MonthlyFlowChart from "./MonthlyFlowChart";
import SegmentTrendChart from "./SegmentTrendChart";

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount || 0);

const formatNumber = (value) => new Intl.NumberFormat("en-US").format(value || 0);
const formatPercent = (value) => `${Number(value || 0).toFixed(1)}%`;

const hasData = (rows, keys) =>
  Array.isArray(rows) && rows.some((row) => keys.some((key) => Math.abs(Number(row[key] || 0)) > 0));

const SegmentDashboard = ({ segment, metrics, isLoading, periodLabel }) => {
  const accent = segment.accent;
  const border = segment.borderColor;

  return (
    <Box>
      {/* KPIs principais */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <DashboardKpiCard
            isHero
            isLoading={isLoading}
            title={`${segment.label} Clients`}
            value={formatNumber(metrics.clients)}
            caption="active clients"
            accentColor={accent}
            borderColor={border}
            icon={<GroupsIcon sx={{ fontSize: 28 }} />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <DashboardKpiCard
            isHero
            isLoading={isLoading}
            title={`${segment.label} AUM`}
            value={formatCurrency(metrics.aum)}
            caption="current total balance"
            accentColor={accent}
            borderColor={border}
            icon={<AccountBalanceIcon sx={{ fontSize: 28 }} />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <DashboardKpiCard
            isHero
            isLoading={isLoading}
            title="Net Flow"
            value={formatCurrency(metrics.netFlow)}
            caption={`${periodLabel}`}
            delta={metrics.deltas?.netFlow}
            accentColor="#00BA2D"
            borderColor="rgba(0, 186, 45, 0.28)"
            icon={<TrendingUpIcon sx={{ fontSize: 28 }} />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <DashboardKpiCard
            isHero
            isLoading={isLoading}
            title="Performance Paid"
            value={formatCurrency(metrics.performancePaid)}
            caption={`${periodLabel}`}
            delta={metrics.deltas?.performancePaid}
            accentColor="#3A87DE"
            borderColor="rgba(58, 135, 222, 0.28)"
            icon={<ShowChartIcon sx={{ fontSize: 28 }} />}
          />
        </Grid>
      </Grid>

      {/* KPIs secundários */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <DashboardKpiCard
            isLoading={isLoading}
            title="Deposits"
            value={formatCurrency(metrics.deposits)}
            caption={
              metrics.routingIn > 0
                ? `${periodLabel} · excl. routing ${formatCurrency(metrics.routingIn)}`
                : periodLabel
            }
            accentColor="#2AF598"
            borderColor="rgba(42, 245, 152, 0.2)"
            icon={<TrendingUpIcon sx={{ fontSize: 24 }} />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <DashboardKpiCard
            isLoading={isLoading}
            title="Withdrawals"
            value={formatCurrency(metrics.withdrawals)}
            caption={
              metrics.routingOut > 0
                ? `${periodLabel} · excl. routing ${formatCurrency(metrics.routingOut)}`
                : periodLabel
            }
            accentColor="#F52A2D"
            borderColor="rgba(245, 42, 45, 0.2)"
            icon={<TrendingDownIcon sx={{ fontSize: 24 }} />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <DashboardKpiCard
            isLoading={isLoading}
            title="Yield Adoption"
            value={formatPercent(metrics.yieldAdoption)}
            caption={`${formatNumber(metrics.yieldParticipants)} active in yield`}
            accentColor="#FFC107"
            borderColor="rgba(255, 193, 7, 0.2)"
            icon={<SavingsIcon sx={{ fontSize: 24 }} />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <DashboardKpiCard
            isLoading={isLoading}
            title="App Up-to-date"
            value={formatPercent(metrics.appAdoptionPct)}
            caption={`${formatNumber(metrics.appUpToDate)} up / ${formatNumber(
              metrics.appOutdated
            )} old / ${formatNumber(metrics.appUnknown)} unknown`}
            accentColor="#2AF598"
            borderColor="rgba(42, 245, 152, 0.2)"
            icon={<PhoneIphoneIcon sx={{ fontSize: 24 }} />}
          />
        </Grid>
      </Grid>

      {/* Gráficos do segmento */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} lg={7}>
          <DashboardChartCard
            title="Monthly Deposits vs Withdrawals"
            subtitle="Approved deposits and paid withdrawals only, by month."
            isLoading={isLoading}
            isEmpty={!hasData(metrics.monthly, ["deposits", "withdrawals"])}
          >
            <MonthlyFlowChart data={metrics.monthly} />
          </DashboardChartCard>
        </Grid>
        <Grid item xs={12} lg={5}>
          <DashboardChartCard
            title="AUM Evolution"
            subtitle="Monthly closings when available; latest balance as fallback."
            isLoading={isLoading}
            isEmpty={!hasData(metrics.monthly, ["aum"])}
          >
            <SegmentTrendChart data={metrics.monthly} dataKey="aum" label="AUM" color={accent} />
          </DashboardChartCard>
        </Grid>
        <Grid item xs={12}>
          <DashboardChartCard
            title="Performance Paid (monthly)"
            subtitle="Performance credited to clients per month (ledger entries)."
            isLoading={isLoading}
            isEmpty={!hasData(metrics.monthly, ["performance"])}
          >
            <SegmentTrendChart
              data={metrics.monthly}
              dataKey="performance"
              label="Performance Paid"
              color="#3A87DE"
            />
          </DashboardChartCard>
        </Grid>
      </Grid>

      {/* Yield do segmento */}
      <Box sx={{ mb: 1 }}>
        <Typography sx={{ color: "#fff", fontSize: { xs: "16px", md: "18px" }, fontWeight: 700, mb: 1.5 }}>
          {segment.label} Yield
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <DashboardKpiCard
              isLoading={isLoading}
              title="Yield Participants"
              value={formatNumber(metrics.yieldParticipants)}
              icon={<GroupsIcon sx={{ fontSize: 24 }} />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <DashboardKpiCard
              isLoading={isLoading}
              title="Net Yield"
              value={formatCurrency(metrics.netYield)}
              icon={<SavingsIcon sx={{ fontSize: 24 }} />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <DashboardKpiCard
              isLoading={isLoading}
              title="Gross Yield"
              value={formatCurrency(metrics.grossYield)}
              icon={<ShowChartIcon sx={{ fontSize: 24 }} />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <DashboardKpiCard
              isLoading={isLoading}
              title="Yield Revenue"
              value={formatCurrency(metrics.yieldRevenue)}
              caption="commissions earned"
              accentColor="#FFC107"
              borderColor="rgba(255, 193, 7, 0.2)"
              icon={<AttachMoneyIcon sx={{ fontSize: 24 }} />}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default SegmentDashboard;
